import { createFileRoute } from "@tanstack/react-router";
import { motion } from "motion/react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Mail, MapPin, Phone, Send, MessageCircle, CheckCircle2 } from "lucide-react";
import { useState } from "react";
import { toast, Toaster } from "sonner";
import { submitContactForm } from "@/lib/api/contact";

export const Route = createFileRoute("/contacto")({
  head: () => ({
    meta: [
      { title: "Contacto — PRICO" },
      { name: "description", content: "Escríbenos: teléfono, correo, dirección y formulario para contactar al equipo de PRICO." },
      { property: "og:title", content: "Contacto — PRICO" },
      { property: "og:description", content: "Estamos a un mensaje de distancia." },
      { property: "og:url", content: "/contacto" },
    ],
    links: [{ rel: "canonical", href: "/contacto" }],
  }),
  component: ContactPage,
});

const schema = z.object({
  name: z.string().trim().min(2, "Ingresa tu nombre").max(80),
  email: z.string().trim().email("Correo inválido").max(120),
  phone: z.string().trim().max(30).optional().or(z.literal("")),
  subject: z.string().trim().min(2, "Asunto requerido").max(120),
  message: z.string().trim().min(10, "Cuéntanos un poco más").max(1000),
  hp: z.string().optional(),
});
type Form = z.infer<typeof schema>;

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-80px" },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] as const },
};

function ContactPage() {
  const [sent, setSent] = useState(false);
  const { register, handleSubmit, formState: { errors, isSubmitting }, reset } = useForm<Form>({
    resolver: zodResolver(schema),
  });

  const onSubmit = async (data: Form) => {
    const result = await submitContactForm({
      name: data.name,
      email: data.email,
      phone: data.phone ?? "",
      subject: '[PRICO Contact from Website] ' + data.subject,
      message: data.message,
    });

    if (result.ok) {
      toast.success("¡Mensaje enviado! Te contactaremos pronto.");
      setSent(true);
      reset();
      return;
    }

    if (result.status === 429) {
      toast.error("Demasiados intentos. Intenta de nuevo en unos minutos.");
    } else {
      toast.error("Error al enviar el mensaje. Intenta de nuevo más tarde.");
    }
  };

  return (
    <>
      <Toaster richColors position="top-center" />
      <section className="relative overflow-hidden grain">
        <div className="absolute inset-0 -z-10" style={{ background: "var(--gradient-warm)" }} />
        <div className="container-prico py-20 md:py-24 text-center max-w-3xl mx-auto">
          <motion.div {...fadeUp}>
            <span className="chip">Contacto</span>
            <h1 className="text-5xl md:text-7xl font-black mt-5 leading-[1]">Hablemos.</h1>
            <p className="mt-6 text-lg text-muted-foreground">
              ¿Tienes una pregunta, propuesta o quieres ser aliado? Te respondemos en menos de 24h.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="container-prico pb-24 grid lg:grid-cols-[1fr_1.2fr] gap-8">
        <motion.div {...fadeUp} className="space-y-4">
          {[
            { i: Phone, t: "Teléfono", d: "+57 316-320-1208 ", h: "tel:+573163201208 " },
            { i: Mail, t: "Correo", d: "support@pricoapp.com", h: "mailto:support@pricoapp.com" },
            { i: MapPin, t: "Ubicación", d: "Medellín, Colombia", h: "#" },
            { i: MessageCircle, t: "WhatsApp", d: "Chatea con nosotros", h: "https://wa.me/573163201208 " },
          ].map((c) => (
            <a key={c.t} href={c.h} className="card-soft p-5 flex items-center gap-4 hover:border-primary transition group">
              <div className="size-12 rounded-2xl grid place-items-center text-primary-foreground shrink-0" style={{ background: "var(--gradient-primary)" }}>
                <c.i className="size-6" />
              </div>
              <div className="min-w-0">
                <p className="text-xs uppercase tracking-wider text-muted-foreground">{c.t}</p>
                <p className="font-semibold group-hover:text-primary truncate">{c.d}</p>
              </div>
            </a>
          ))}
          <div className="card-soft p-5">
            <p className="text-xs uppercase tracking-wider text-muted-foreground">Horario</p>
            <p className="font-semibold mt-1">Lunes a Viernes · 8:00 a.m. – 6:00 p.m.</p>
            <p className="text-sm text-muted-foreground mt-1">Sábados · 9:00 a.m. – 1:00 p.m.</p>
          </div>
        </motion.div>

        <motion.div {...fadeUp} className="card-soft p-8 md:p-10">
          {sent ? (
            <div className="text-center py-10">
              <CheckCircle2 className="size-16 text-[var(--success)] mx-auto" />
              <h3 className="text-2xl font-black mt-4">¡Mensaje recibido!</h3>
              <p className="text-muted-foreground mt-2">Te escribimos pronto a tu correo.</p>
              <button onClick={() => setSent(false)} className="btn-ghost mt-6 text-sm">Enviar otro mensaje</button>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              {/* Honeypot — hidden from real users, traps bots */}
              <input
                {...register("hp")}
                type="text"
                tabIndex={-1}
                aria-hidden="true"
                style={{ display: "none" }}
              />
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Nombre" error={errors.name?.message}>
                  <input {...register("name")} className="input-field" placeholder="Tu nombre" />
                </Field>
                <Field label="Correo" error={errors.email?.message}>
                  <input {...register("email")} type="email" className="input-field" placeholder="tu@correo.com" />
                </Field>
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                <Field label="Teléfono (opcional)" error={errors.phone?.message}>
                  <input {...register("phone")} className="input-field" placeholder="+57 316-320-1208 " />
                </Field>
                <Field label="Asunto" error={errors.subject?.message}>
                  <input {...register("subject")} className="input-field" placeholder="¿En qué podemos ayudarte?" />
                </Field>
              </div>
              <Field label="Mensaje" error={errors.message?.message}>
                <textarea {...register("message")} rows={5} className="input-field resize-none" placeholder="Cuéntanos..." />
              </Field>
              <p className="text-xs text-muted-foreground">
                Al enviar aceptas nuestra política de privacidad. Tus datos están protegidos por SSL.
              </p>
              <button type="submit" disabled={isSubmitting} className="btn-primary w-full disabled:opacity-60">
                {isSubmitting ? "Enviando..." : <>Enviar mensaje <Send className="size-4" /></>}
              </button>
            </form>
          )}
        </motion.div>
      </section>

      <style>{`
        .input-field {
          width: 100%;
          padding: 0.75rem 1rem;
          border-radius: 0.85rem;
          border: 1.5px solid var(--border);
          background: var(--background);
          font-size: 0.95rem;
          transition: border-color .2s ease, box-shadow .2s ease;
        }
        .input-field:focus {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 4px color-mix(in oklab, var(--primary) 18%, transparent);
        }
      `}</style>
    </>
  );
}

function Field({ label, error, children }: { label: string; error?: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="text-sm font-semibold block mb-1.5">{label}</span>
      {children}
      {error && <span className="text-xs text-destructive mt-1 block">{error}</span>}
    </label>
  );
}
